import { memo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Sparkles, User, ExternalLink, FileText, Inbox, Search, CheckSquare,
  ChevronDown, Copy, Check, AlertTriangle, HelpCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import type { StepState, StepPhase, AnalysisPayload, PromptDraftPayload, PastedOutputPayload } from '@/lib/workflowStreamProtocol';

interface StepCardProps {
  step: StepState;
  index: number;
  isActive: boolean;
  onFocus?: (stepId: string) => void;
}

const TYPE_CONFIG = {
  manual: { label: 'Manual', icon: User, className: 'bg-muted text-muted-foreground border-muted' },
  ai_assisted: { label: 'AI', icon: Sparkles, className: 'bg-primary/10 text-primary border-primary/20' },
  other: { label: 'External', icon: ExternalLink, className: 'bg-secondary text-secondary-foreground border-secondary' },
} as const;

const PHASE_LABEL: Record<StepPhase, string> = {
  proposed: 'proposed',
  prompt_drafting: 'drafting prompt',
  prompt_ready: 'prompt ready',
  awaiting_output: 'awaiting output',
  analyzing: 'analyzing',
  done: 'done',
};

const PHASE_PILL: Record<StepPhase, string> = {
  proposed: 'bg-muted text-muted-foreground',
  prompt_drafting: 'bg-primary/10 text-primary animate-pulse',
  prompt_ready: 'bg-primary/15 text-primary',
  awaiting_output: 'bg-warning/10 text-warning-foreground',
  analyzing: 'bg-info/10 text-info-foreground animate-pulse',
  done: 'bg-success/15 text-success-foreground',
};

const PREVIEW_LINES = 5;

function firstNLines(text: string, n: number): { preview: string; hasMore: boolean } {
  const lines = text.split('\n');
  if (lines.length <= n) return { preview: text, hasMore: false };
  return { preview: lines.slice(0, n).join('\n'), hasMore: true };
}

// ── Slot ─────────────────────────────────────────────────────────────

interface SlotProps {
  icon: typeof FileText;
  label: string;
  meta: string;
  state: 'pending' | 'active' | 'done';
  /** When true, body is hidden by default but a chevron lets the user peek. */
  defaultCollapsed?: boolean;
  children?: React.ReactNode;
}

function Slot({ icon: Icon, label, meta, state, defaultCollapsed, children }: SlotProps) {
  const hasContent = !!children;
  const [expanded, setExpanded] = useState(!defaultCollapsed);
  return (
    <div className="border-t border-dashed border-border/60 first:border-t-0">
      <button
        type="button"
        onClick={(e) => { if (!hasContent) return; e.stopPropagation(); setExpanded(v => !v); }}
        disabled={!hasContent}
        className={cn(
          'w-full flex items-center gap-2 px-3 py-2 text-xs text-left',
          state === 'done' && 'text-foreground',
          state === 'active' && 'text-primary',
          state === 'pending' && 'text-muted-foreground',
          hasContent && 'hover:bg-muted/30 cursor-pointer',
        )}
      >
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span className="font-medium flex-1">{label}</span>
        <span className="text-[11px] opacity-70">{meta}</span>
        {hasContent && (
          <ChevronDown className={cn('h-3 w-3 opacity-50 transition-transform', expanded && 'rotate-180')} />
        )}
      </button>
      {hasContent && expanded && (
        <div className="px-3 pb-2">{children}</div>
      )}
    </div>
  );
}

// ── Filled-slot contents ─────────────────────────────────────────────

function PromptSlotContent({ draft }: { draft: PromptDraftPayload }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const { preview, hasMore } = firstNLines(draft.body, PREVIEW_LINES);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(draft.body);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
      toast.success('Prompt copied');
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <div className="rounded-md border border-primary/15 bg-primary/5 overflow-hidden min-w-0">
      <pre className={cn(
        'px-2.5 py-2 text-[11px] font-mono leading-relaxed whitespace-pre-wrap break-words text-foreground',
        !expanded && 'max-h-32 overflow-hidden',
        expanded && 'max-h-72 overflow-auto',
      )}>
        {expanded ? draft.body : preview}
      </pre>
      <div className="flex items-center justify-between gap-2 px-2 py-1 border-t border-primary/15 bg-card">
        <span className="text-[10px] text-muted-foreground truncate min-w-0">
          {expanded ? `v${draft.version} · full prompt` : hasMore ? `v${draft.version} · showing first ${PREVIEW_LINES} lines` : `v${draft.version}`}
        </span>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={handleCopy}
            className="h-6 px-1.5 gap-1 text-[10px]"
          >
            {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          </Button>
          {hasMore && (
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => { e.stopPropagation(); setExpanded(v => !v); }}
              className="h-6 px-1.5 gap-1 text-[10px]"
            >
              <ChevronDown className={cn('h-3 w-3 transition-transform', expanded && 'rotate-180')} />
              {expanded ? 'less' : 'expand'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function OutputSlotContent({ output }: { output: PastedOutputPayload }) {
  const [expanded, setExpanded] = useState(false);
  const { preview, hasMore } = firstNLines(output.text, PREVIEW_LINES);
  return (
    <div className="rounded-md border border-border/60 bg-muted/30 overflow-hidden min-w-0">
      <pre className={cn(
        'px-2.5 py-2 text-[11px] font-mono leading-relaxed whitespace-pre-wrap break-words text-muted-foreground',
        expanded && 'max-h-72 overflow-auto',
      )}>
        {expanded ? output.text.slice(0, 4000) + (output.text.length > 4000 ? '\n…' : '') : preview}
      </pre>
      {hasMore && (
        <div className="flex justify-end px-2 py-1 border-t border-border/60 bg-card">
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => { e.stopPropagation(); setExpanded(v => !v); }}
            className="h-6 px-1.5 gap-1 text-[10px]"
          >
            <ChevronDown className={cn('h-3 w-3 transition-transform', expanded && 'rotate-180')} />
            {expanded ? 'less' : 'expand'}
          </Button>
        </div>
      )}
    </div>
  );
}

function AnalysisSlotContent({ analysis }: { analysis: AnalysisPayload }) {
  const [expanded, setExpanded] = useState(false);
  const sectionCount =
    analysis.sections.looksGood.length +
    analysis.sections.worthFlagging.length +
    analysis.sections.decisions.length;

  return (
    <div className="rounded-md border border-primary/15 bg-primary/5 overflow-hidden">
      <div className="px-2.5 py-2 text-[12px] font-medium">{analysis.summary.headline}</div>
      {expanded && (
        <div className="px-2.5 pb-2 space-y-2 text-[11px]">
          {analysis.sections.looksGood.length > 0 && (
            <MiniSection title="Looks good" icon={Check} iconClass="text-success" items={analysis.sections.looksGood.map(b => b.text)} />
          )}
          {analysis.sections.worthFlagging.length > 0 && (
            <MiniSection title="Worth flagging" icon={AlertTriangle} iconClass="text-warning" items={analysis.sections.worthFlagging.map(b => b.text)} />
          )}
          {analysis.sections.decisions.length > 0 && (
            <MiniSection
              title="Decisions"
              icon={HelpCircle}
              iconClass="text-info"
              items={analysis.sections.decisions.map(d =>
                d.recommendedOption ? `${d.text} (recommended: ${d.recommendedOption})` : d.text
              )}
            />
          )}
        </div>
      )}
      <div className="flex justify-end px-2 py-1 border-t border-primary/15 bg-card">
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => { e.stopPropagation(); setExpanded(v => !v); }}
          className="h-6 px-1.5 gap-1 text-[10px]"
        >
          <ChevronDown className={cn('h-3 w-3 transition-transform', expanded && 'rotate-180')} />
          {expanded ? 'less' : `expand · ${sectionCount} items`}
        </Button>
      </div>
    </div>
  );
}

function MiniSection({
  title, icon: Icon, iconClass, items,
}: {
  title: string;
  icon: typeof Check;
  iconClass: string;
  items: string[];
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
        <Icon className={`h-2.5 w-2.5 ${iconClass}`} />
        {title}
      </div>
      <ul className="pl-3 list-disc space-y-0.5 marker:text-muted-foreground">
        {items.map((t, i) => <li key={i} className="text-foreground/90 text-[11px] leading-snug">{t}</li>)}
      </ul>
    </div>
  );
}

function DecisionsSlotContent({ decisions, pending }: { decisions: { question: string; chosen: string }[]; pending: { text: string; recommendedOption?: string }[] }) {
  return (
    <ul className="rounded-md border border-border/60 bg-muted/30 px-3 py-2 space-y-1 text-[11px]">
      {decisions.map((d, i) => (
        <li key={`d-${i}`} className="text-foreground">
          <span className="text-muted-foreground">{d.question}: </span>
          <span className="font-medium">{d.chosen}</span>
        </li>
      ))}
      {pending.map((d, i) => (
        <li key={`p-${i}`} className="text-muted-foreground italic">
          {d.text}{d.recommendedOption ? ` — recommend ${d.recommendedOption}` : ''}
        </li>
      ))}
    </ul>
  );
}

// ── StepCard ─────────────────────────────────────────────────────────

export const StepCard = memo(function StepCard({ step, index, isActive, onFocus }: StepCardProps) {
  const typeConfig = TYPE_CONFIG[step.type];
  const TypeIcon = typeConfig.icon;
  const isProposed = step.phase === 'proposed';
  const isDone = step.phase === 'done';

  const slotStates = {
    prompt: step.promptDraft ? 'done' : isActive && step.phase === 'prompt_drafting' ? 'active' : 'pending',
    output: step.pastedOutput ? 'done' : isActive && (step.phase === 'awaiting_output' || step.phase === 'prompt_ready') ? 'active' : 'pending',
    analysis: step.analysis ? 'done' : isActive && step.phase === 'analyzing' ? 'active' : 'pending',
    decisions: step.decisions.length > 0 || (step.analysis?.sections.decisions.length ?? 0) > 0 ? 'done' : 'pending',
  } as const;

  // Determine the primary slot — the most-recently populated one stays expanded
  // by default. Earlier filled slots collapse to a header-only row so the user
  // can scan all four slots without scrolling. Click any header to peek inside.
  const primarySlot: 'prompt' | 'output' | 'analysis' | 'decisions' | null =
    slotStates.decisions === 'done' ? 'decisions'
    : slotStates.analysis === 'done' ? 'analysis'
    : slotStates.output === 'done' ? 'output'
    : slotStates.prompt === 'done' ? 'prompt'
    : null;

  return (
    <Card
      onClick={() => onFocus?.(step.id)}
      className={cn(
        'overflow-hidden cursor-pointer transition-all',
        isProposed && 'border-dashed bg-muted/20',
        isActive && 'border-primary/40 ring-2 ring-primary/10',
        isDone && 'border-success/30',
      )}
    >
      <div className="flex items-start justify-between gap-3 px-3 py-2.5 border-b border-border/60">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-semibold text-base text-foreground/80 leading-none w-6 shrink-0 tabular-nums">
            {index + 1}.
          </span>
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{step.stepName}</div>
            <div className="text-[11px] text-muted-foreground flex items-center gap-1.5 mt-0.5">
              {step.recommendedTool ? <span className="font-medium">{step.recommendedTool}</span> : null}
              {step.estimatedDuration ? <span>· {step.estimatedDuration}</span> : null}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Badge variant="outline" className={cn('gap-1 text-[10px] h-5 px-1.5', typeConfig.className)}>
            <TypeIcon className="h-2.5 w-2.5" />
            {typeConfig.label}
          </Badge>
          <Badge variant="outline" className={cn('text-[10px] h-5 px-1.5', PHASE_PILL[step.phase])}>
            {PHASE_LABEL[step.phase]}
          </Badge>
        </div>
      </div>

      <Slot
        icon={FileText}
        label="prompt"
        meta={step.promptDraft ? `v${step.promptDraft.version}` : '—'}
        state={slotStates.prompt}
        defaultCollapsed={primarySlot !== null && primarySlot !== 'prompt'}
      >
        {step.promptDraft && <PromptSlotContent draft={step.promptDraft} />}
      </Slot>

      <Slot
        icon={Inbox}
        label="output"
        meta={step.pastedOutput
          ? step.pastedOutput.meta?.rows
            ? `${step.pastedOutput.meta.rows} rows`
            : 'received'
          : '—'}
        state={slotStates.output}
        defaultCollapsed={primarySlot !== null && primarySlot !== 'output'}
      >
        {step.pastedOutput && <OutputSlotContent output={step.pastedOutput} />}
      </Slot>

      <Slot
        icon={Search}
        label="analysis"
        meta={step.analysis ? 'reviewed' : '—'}
        state={slotStates.analysis}
        defaultCollapsed={primarySlot !== null && primarySlot !== 'analysis'}
      >
        {step.analysis && <AnalysisSlotContent analysis={step.analysis} />}
      </Slot>

      <Slot
        icon={CheckSquare}
        label="decisions"
        meta={(() => {
          const recorded = step.decisions.length;
          const pending = step.analysis?.sections.decisions.length ?? 0;
          if (recorded > 0) return `${recorded}`;
          if (pending > 0) return `${pending} pending`;
          return '—';
        })()}
        state={slotStates.decisions}
        defaultCollapsed={primarySlot !== null && primarySlot !== 'decisions'}
      >
        {(step.decisions.length > 0 || (step.analysis?.sections.decisions.length ?? 0) > 0) && (
          <DecisionsSlotContent
            decisions={step.decisions}
            pending={step.analysis?.sections.decisions ?? []}
          />
        )}
      </Slot>
    </Card>
  );
});
